import {
  AccountState,
  AccountStatusReason,
  EksClusterStatus,
  KubeAccessStatus,
} from '@repo/api-gw-sdk';

import type { TagStatus } from '@/components/shared/statusTag';

const accountStatuses: Record<
  AccountState,
  {
    title: string;
    icon: string;
    tagStatus: TagStatus;
  }
> = {
  [AccountState.ACCOUNT_STATE_CONNECTED]: {
    title: 'Connected',
    tagStatus: 'active',
    icon: 'material-symbols-check-circle-outline-rounded',
  },
  [AccountState.ACCOUNT_STATE_DISCONNECTED]: {
    title: 'Disconnected',
    tagStatus: 'inactive',
    icon: 'material-symbols-error-med-outline-rounded',
  },
  [AccountState.ACCOUNT_STATE_INSUFFICIENT_PERMISSIONS]: {
    title: 'Permissions Required',
    tagStatus: 'pending',
    icon: 'material-symbols-ads-click',
  },
  [AccountState.ACCOUNT_STATE_NOT_READY]: {
    title: 'Setup Required',
    tagStatus: 'pending',
    icon: 'material-symbols-info-outline',
  },
  [AccountState.ACCOUNT_STATE_UNSPECIFIED]: {
    title: '',
    icon: '',
    tagStatus: 'error',
  },
};

const eksClusterStatuses: Record<EksClusterStatus, { title: string }> = {
  [EksClusterStatus.EKS_CLUSTER_STATUS_ACTIVE]: {
    title: 'Active',
  },
  [EksClusterStatus.EKS_CLUSTER_STATUS_CREATING]: {
    title: 'Creating',
  },
  [EksClusterStatus.EKS_CLUSTER_STATUS_FAILED]: {
    title: 'Failed',
  },
  [EksClusterStatus.EKS_CLUSTER_STATUS_DELETING]: {
    title: 'Deleting',
  },
  [EksClusterStatus.EKS_CLUSTER_STATUS_UPDATING]: {
    title: 'Updating',
  },
  [EksClusterStatus.EKS_CLUSTER_STATUS_PENDING]: {
    title: 'Pending',
  },
  [EksClusterStatus.EKS_CLUSTER_STATUS_UNSPECIFIED]: {
    title: '',
  },
};

const kubeAccessStatuses: Record<
  KubeAccessStatus,
  { title: string; icon: string; tagStatus: TagStatus }
> = {
  [KubeAccessStatus.KUBE_ACCESS_STATUS_FULL_ACCESS]: {
    title: 'Full access',
    tagStatus: 'active',
    icon: 'material-symbols-check-circle-outline-rounded',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_INSUFFICIENT_PERMISSIONS]: {
    title: 'Insufficient permissions',
    tagStatus: 'pending',
    icon: 'material-symbols-ads-click',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_NO_NETWORK_ACCESS]: {
    title: 'No network access',
    tagStatus: 'pending',
    icon: 'material-symbols-info-outline',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_PARTIAL_ACCESS]: {
    title: 'Partial access',
    tagStatus: 'pending',
    icon: 'material-symbols-info-outline',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_IAM_ROLE_MISCONFIGURED]: {
    title: 'IAM role misconfigured',
    tagStatus: 'pending',
    icon: 'material-symbols-ads-click',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_CONFIGMAP_ONLY_ACCESS]: {
    title: 'Action required',
    tagStatus: 'pending',
    icon: 'material-symbols-ads-click',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_ACCESS_DENIED_BY_POLICY]: {
    title: 'Action required',
    tagStatus: 'pending',
    icon: 'material-symbols-ads-click',
  },
  [KubeAccessStatus.KUBE_ACCESS_STATUS_UNSPECIFIED]: {
    title: '',
    icon: '',
    tagStatus: 'error',
  },
};

export const KubeAccessStatuses = Object.fromEntries(
  Object.entries(kubeAccessStatuses)
);
export const EksClusterStatuses = Object.fromEntries(
  Object.entries(eksClusterStatuses)
);
export const AccountStatuses = Object.fromEntries(
  Object.entries(accountStatuses)
);

export const statusReasonTooltips: Partial<
  Record<AccountStatusReason, string>
> = {
  [AccountStatusReason.ACCOUNT_STATUS_REASON_MISSING_INTERNAL_RESOURCE_GROUP]:
    'Eon couldn’t create the resource group needed to back up this subscription.',
};

export interface ConnectionSubject {
  // Source connections hold resources Eon backs up; restore connections are where Eon restores them to.
  kind: 'source' | 'restore';
  // The connection as the row names it, e.g. "AWS account" or "management group".
  noun: string;
  // Where its resources live, e.g. "it" or "its subscriptions".
  scope: string;
}

const SOURCE_STATE_TOOLTIPS: Partial<
  Record<string, (noun: string, scope: string) => string>
> = {
  [AccountState.ACCOUNT_STATE_CONNECTED]: (noun, scope) =>
    `Eon is connected to this ${noun} and backs up the resources in ${scope} according to your backup policies.`,
  [AccountState.ACCOUNT_STATE_DISCONNECTED]: (noun, scope) =>
    `This ${noun} was disconnected from Eon, so the resources in ${scope} aren't backed up. You can still explore and restore any snapshots they have.`,
  [AccountState.ACCOUNT_STATE_INSUFFICIENT_PERMISSIONS]: (noun, scope) =>
    `Eon can no longer access this ${noun}, so the resources in ${scope} aren't being backed up. Update its permissions to restore access, or disconnect it if you no longer want to back it up.`,
  [AccountState.ACCOUNT_STATE_NOT_READY]: (noun, scope) =>
    `Eon can't back up the resources in ${scope} until this ${noun}'s setup is finished. Complete the connection steps to start backing them up.`,
};

const RESTORE_STATE_TOOLTIPS: Partial<
  Record<string, (noun: string, scope: string) => string>
> = {
  [AccountState.ACCOUNT_STATE_CONNECTED]: (noun, scope) =>
    `Eon is connected to this ${noun} and can restore resources into ${scope}.`,
  [AccountState.ACCOUNT_STATE_DISCONNECTED]: (noun, scope) =>
    `This ${noun} was disconnected from Eon, so you can't restore resources into ${scope}. Reconnect it to restore there again.`,
  [AccountState.ACCOUNT_STATE_INSUFFICIENT_PERMISSIONS]: (noun, scope) =>
    `Eon can no longer access this ${noun}, so you can't restore resources into ${scope}. Update its permissions to restore access, or disconnect it if you no longer want to restore there.`,
  [AccountState.ACCOUNT_STATE_NOT_READY]: (noun, scope) =>
    `You can't restore resources into ${scope} until this ${noun}'s setup is finished. Complete the connection steps to start restoring there.`,
};

// Every connection state says what it means and, when blocked, how to unblock it.
export const connectionStateTooltip = (
  // Parent rows report their state as a plain string with the same values.
  state: string | undefined,
  { kind, noun, scope }: ConnectionSubject
) =>
  state
    ? (kind === 'source' ? SOURCE_STATE_TOOLTIPS : RESTORE_STATE_TOOLTIPS)[
        state
      ]?.(noun, scope)
    : undefined;
